/**
 * @fileoverview Modal táctil ultra responsivo para visualizar el Top 20 en vivo de Reacción Rápida Personal.
 * Se conecta a SignalR para refrescar automáticamente la tabla en tiempo real ante nuevos registros o jugadas ('personalQuickReaction:newPlay').
 * 
 * @module components/Dashboard/Sections/PersonalQuickReactionRankingModal
 */

import React, { useState, useEffect, useCallback } from 'react';
import SignalRService from '../../../utilities/WS/SignalRService';
import { GetRankingBL, getReactionBadge } from '../../../utilities/BL/PersonalQuickReaction';

/**
 * Componente modal táctil para visualización del Ranking Top 20 en vivo.
 * 
 * @param {Object} props - Propiedades del modal.
 * @param {boolean} props.isOpen - Estado de visibilidad del modal.
 * @param {Function} props.onClose - Función callback al cerrar el modal.
 * @param {string} props.roomId - Identificador GUID de la sala activa.
 * @param {string} [props.currentParticipantId] - Identificador GUID del participante actual para resaltarlo.
 * @returns {React.ReactElement|null} Modal renderizado o null si está cerrado.
 */
export const PersonalQuickReactionRankingModal = ({
    isOpen,
    onClose,
    roomId,
    currentParticipantId
}) => {
    const [rankingList, setRankingList] = useState([]);
    const [totalParticipants, setTotalParticipants] = useState(0);
    const [winnerItem, setWinnerItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    /**
     * Consulta el ranking actualizado desde la API REST.
     */
    const fetchRanking = useCallback(async () => {
        if (!roomId) return;
        setIsLoading(true);
        try {
            const response = await GetRankingBL(roomId, 20);
            if (response && response.success && response.data) {
                const data = response.data;
                setRankingList(Array.isArray(data.ranking) ? data.ranking : []);
                setTotalParticipants(Number(data.totalParticipants) || 0);
                setWinnerItem(data.winner || null);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.warn('[PersonalQuickReactionRankingModal] Error al consultar ranking:', error);
        } finally {
            setIsLoading(false);
        }
    }, [roomId]);

    // Cargar ranking al abrir el modal
    useEffect(() => {
        if (isOpen && roomId) {
            fetchRanking();
        }
    }, [isOpen, roomId, fetchRanking]);

    // Suscripción a eventos SignalR en tiempo real
    useEffect(() => {
        if (!isOpen || !roomId) return;

        const handleRealtimeNewPlay = (payload) => {
            // Refrescar ranking cuando ocurra una nueva jugada en la sala
            fetchRanking();
        };

        const handleRealtimeReset = (payload) => {
            fetchRanking();
        };

        SignalRService.on('personalQuickReaction:newPlay', handleRealtimeNewPlay);
        SignalRService.on('personalQuickReaction:reset', handleRealtimeReset);

        return () => {
            SignalRService.off('personalQuickReaction:newPlay', handleRealtimeNewPlay);
            SignalRService.off('personalQuickReaction:reset', handleRealtimeReset);
        };
    }, [isOpen, roomId, fetchRanking]);

    if (!isOpen) return null;

    return (
        <div className="pqr-modal-overlay" onClick={onClose}>
            <div
                className="pqr-modal-card"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pqr-ranking-title"
            >
                {/* CABECERA DEL MODAL */}
                <div className="pqr-modal-card__header">
                    <div className="pqr-modal-card__header-titles">
                        <div className="pqr-modal-card__live-tag">
                            <span className="pqr-modal-card__live-dot" />
                            EN VIVO
                        </div>
                        <h3 id="pqr-ranking-title" className="pqr-modal-card__title">
                            🏆 Top 20 de Velocidad
                        </h3>
                        <p className="pqr-modal-card__subtitle">
                            Total participantes: <strong>{totalParticipants}</strong>
                            {lastUpdated && (
                                <span className="pqr-modal-card__time-tag">
                                    {' '}• Actualizado: {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="pqr-modal-card__header-actions">
                        <button
                            type="button"
                            className="pqr-modal-card__refresh-btn"
                            onClick={fetchRanking}
                            disabled={isLoading}
                            title="Refrescar ranking"
                            aria-label="Refrescar ranking"
                        >
                            <i className={`bi bi-arrow-clockwise ${isLoading ? 'pqr-spin-anim' : ''}`} />
                        </button>
                        <button
                            type="button"
                            className="pqr-modal-card__close-btn"
                            onClick={onClose}
                            aria-label="Cerrar modal"
                        >
                            <i className="bi bi-x-lg" />
                        </button>
                    </div>
                </div>

                {/* CONTENIDO SCROLLABLE DEL RANKING */}
                <div className="pqr-modal-card__body">
                    {isLoading && rankingList.length === 0 ? (
                        <div className="pqr-modal-card__loading-box">
                            <div className="pqr-spinner" />
                            <p>Cargando posiciones oficiales...</p>
                        </div>
                    ) : rankingList.length === 0 ? (
                        <div className="pqr-modal-card__empty-box">
                            <i className="bi bi-stopwatch pqr-modal-card__empty-icon" />
                            <h4>Aún no hay jugadas registradas</h4>
                            <p>¡Sé el primer aficionado en desenfundar y marcar el récord de la jornada!</p>
                        </div>
                    ) : (
                        <div className="pqr-ranking-table">
                            {rankingList.map((item, index) => {
                                const position = item.position || (index + 1);
                                const isCurrentPlayer = currentParticipantId && (item.participantId === currentParticipantId);
                                const totalTime = item.totalTimeMs || (item.reactionTimeMs + (item.penaltyTimeMs || 0));
                                const badgeInfo = getReactionBadge(item.reactionTimeMs);

                                let positionBadgeClass = 'pqr-rank-num';
                                let positionIcon = null;

                                if (position === 1) {
                                    positionBadgeClass = 'pqr-rank-num is-first';
                                    positionIcon = <i className="bi bi-trophy-fill pqr-trophy-gold" />;
                                } else if (position === 2) {
                                    positionBadgeClass = 'pqr-rank-num is-second';
                                    positionIcon = <i className="bi bi-award-fill pqr-trophy-silver" />;
                                } else if (position === 3) {
                                    positionBadgeClass = 'pqr-rank-num is-third';
                                    positionIcon = <i className="bi bi-award-fill pqr-trophy-bronze" />;
                                }

                                return (
                                    <div
                                        key={item.participantId || index}
                                        className={`pqr-ranking-row ${position <= 3 ? 'is-podium' : ''} ${isCurrentPlayer ? 'is-highlighted' : ''}`}
                                    >
                                        <div className="pqr-ranking-row__col-rank">
                                            <span className={positionBadgeClass}>
                                                {positionIcon || `#${position}`}
                                            </span>
                                        </div>

                                        <div className="pqr-ranking-row__col-info">
                                            <div className="pqr-ranking-row__name-line">
                                                <span className="pqr-ranking-row__name">
                                                    {item.participantName || 'Aficionado'}
                                                </span>
                                                {isCurrentPlayer && (
                                                    <span className="pqr-ranking-row__you-pill">TÚ</span>
                                                )}
                                            </div>
                                            <div className="pqr-ranking-row__badge-line">
                                                <span
                                                    className="pqr-ranking-row__badge-chip"
                                                    style={{ color: badgeInfo.color }}
                                                >
                                                    <i className={`bi ${badgeInfo.icon}`} /> {badgeInfo.title}
                                                </span>
                                                {item.penaltyTimeMs > 0 && (
                                                    <span className="pqr-ranking-row__penalty-chip">
                                                        +{item.penaltyTimeMs}ms penaliz.
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="pqr-ranking-row__col-time">
                                            <span className="pqr-ranking-row__time-value">
                                                {totalTime}
                                                <span className="pqr-ranking-row__time-unit">ms</span>
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* PIE DEL MODAL */}
                <div className="pqr-modal-card__footer">
                    <button
                        type="button"
                        className="pqr-modal-card__btn-close"
                        onClick={onClose}
                    >
                        Cerrar y Volver
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PersonalQuickReactionRankingModal;
